// ============================================
// SITE-WIDE SEO CONFIGURATION
// ============================================

export const SITE_CONFIG = {
    name: "Vintage Glass Works",
    tagline: "Historic Glass & Door Restoration",
    url: "https://www.vintageglassworks.com",
    foundingDate: "2009",
    phone: "(207) 588-7000",
    email: "info@vintageglassworks.com",
    address: {
        street: "521 Water St",
        city: "Gardiner",
        state: "ME",
        zip: "04345",
        country: "US"
    },
    geo: {
        latitude: 44.2298,
        longitude: -69.7756
    },
    social: {
        facebook: "https://www.facebook.com/vintageglassworks",
        instagram: "https://www.instagram.com/vintageglassworks",
        linkedin: "https://www.linkedin.com/company/vintage-glass-works"
    },
    images: {
        og: "/images/vgw-hero.jpg",
        logo: "/images/vgw-logo.png"
    },
    openingHours: "Mo-Fr 08:00-17:00"
};

export const DEFAULT_SEO = {
    title: `${SITE_CONFIG.name} | ${SITE_CONFIG.tagline}`,
    description: "Expert stained glass restoration, leaded glass repair, and historic window preservation serving churches, universities, and historic buildings throughout the United States since 2009.",
    keywords: [
        "stained glass restoration",
        "leaded glass repair",
        "historic window preservation",
        "wood sash restoration",
        "historic door restoration"
    ]
};

// ============================================
// SCHEMA GENERATORS
// ============================================

export const schemas = {
    // Organization schema for homepage
    organization: () => ({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": SITE_CONFIG.name,
        "image": `${SITE_CONFIG.url}${SITE_CONFIG.images.og}`,
        "url": SITE_CONFIG.url,
        "telephone": SITE_CONFIG.phone,
        "email": SITE_CONFIG.email,
        "address": {
            "@type": "PostalAddress",
            "streetAddress": SITE_CONFIG.address.street,
            "addressLocality": SITE_CONFIG.address.city,
            "addressRegion": SITE_CONFIG.address.state,
            "postalCode": SITE_CONFIG.address.zip,
            "addressCountry": SITE_CONFIG.address.country
        },
        "areaServed": {
            "@type": "Country",
            "name": "United States"
        },
        "sameAs": Object.values(SITE_CONFIG.social),
        "foundingDate": SITE_CONFIG.foundingDate,
        "knowsAbout": DEFAULT_SEO.keywords,
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": SITE_CONFIG.geo.latitude,
            "longitude": SITE_CONFIG.geo.longitude
        },
        "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "17:00"
        },
    }),

    // Service schema for service pages
    service: (service) => ({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": service.title,
        "description": service.seo?.description || service.description,
        "provider": {
            "@type": "LocalBusiness",
            "name": SITE_CONFIG.name,
            "address": {
                "@type": "PostalAddress",
                "streetAddress": SITE_CONFIG.address.street,
                "addressLocality": SITE_CONFIG.address.city,
                "addressRegion": SITE_CONFIG.address.state,
                "postalCode": SITE_CONFIG.address.zip
            }
        },
        "areaServed": {
            "@type": "Country",
            "name": "United States"
        },
        "serviceType": service.category
    }),

    // Project/Article schema for portfolio pages
    project: (project) => ({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": project.title,
        "description": project.seo?.description || project.description,
        "image": project.images.map(img => `${SITE_CONFIG.url}${img}`),
        "author": {
            "@type": "Organization",
            "name": SITE_CONFIG.name
        },
        "publisher": {
            "@type": "Organization",
            "name": SITE_CONFIG.name,
            "logo": {
                "@type": "ImageObject",
                "url": `${SITE_CONFIG.url}${SITE_CONFIG.images.logo}`
            }
        }
    }),

    // Breadcrumb schema
    breadcrumb: (items) => ({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": `${SITE_CONFIG.url}${item.path}`
        }))
    })
};

// ============================================
// META TAG HELPERS
// ============================================

export const getPageMeta = (page) => {
    const defaults = {
        title: DEFAULT_SEO.title,
        description: DEFAULT_SEO.description,
        image: `${SITE_CONFIG.url}${SITE_CONFIG.images.og}`,
        url: SITE_CONFIG.url,
        type: "website"
    };

    return { ...defaults, ...page };
};

export const getServiceMeta = (service, slug) => ({
    title: `${service.seo?.title || service.title} | ${SITE_CONFIG.name}`,
    description: service.seo?.description || service.description,
    image: `${SITE_CONFIG.url}${service.images[0]}`,
    url: `${SITE_CONFIG.url}/services/${slug}`,
    type: "website"
});

export const getProjectMeta = (project, slug) => ({
    title: `${project.seo?.title || project.title} | ${SITE_CONFIG.name}`,
    description: project.seo?.description || project.description,
    image: `${SITE_CONFIG.url}${project.images[0]}`,
    url: `${SITE_CONFIG.url}/portfolio/${slug}`,
    type: "article"
});